import { processVideoUrl, toProxiedDownloadUrl } from "./videoUrl";

/** 当前 TypeScript DOM 类型可能未包含 File System Access 的 createWritable */
type FileSystemFileHandleWithWritable = FileSystemFileHandle & {
  createWritable(): Promise<FileSystemWritableFileStream>;
};

interface FileSystemWritableFileStream extends WritableStream {
  write(data: Blob | BufferSource): Promise<void>;
  close(): Promise<void>;
}

const FETCH_TIMEOUT_MS = 30 * 60 * 1000;

export type BatchVideoItem = { url: string; baseName: string };

export type BatchDownloadProgress = {
  phase: "download" | "zip" | "done";
  /** 已完成集数 */
  current: number;
  total: number;
  message: string;
  /** 当前集内下载进度 0–1（未知总大小时无此项） */
  fileProgress?: number;
};

export type BatchDownloadResult = {
  success: number;
  fail: number;
  /** 已不再使用 ZIP，恒为 false，保留字段以兼容旧调用方 */
  usedZip: boolean;
  aborted: boolean;
  /** 浏览器不支持 showDirectoryPicker */
  unsupportedDirectoryPicker?: boolean;
};

export type WriteUrlProgressCallback = (loaded: number, total: number | null) => void;

export function sanitizeVideoFileBaseName(name: string): string {
  return (name || "video").replace(/[<>:"/\\|?*]/g, "_");
}

function appendCacheBust(url: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_dl=${Date.now()}`;
}

function formatMb(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(1);
}

/**
 * 通过 <a download> 触发浏览器下载；导出 CDN 直链不走 fetch，避免 CORS。
 * 跨域时 download 文件名可能被忽略，以 CDN 响应头/URL 为准。
 */
export function triggerUrlDownload(fileUrl: string, downloadName?: string): void {
  const trimmed = String(fileUrl || "").trim();
  if (!trimmed) return;
  const link = document.createElement("a");
  link.href = trimmed;
  if (downloadName) {
    link.download = downloadName;
  }
  link.rel = "noopener noreferrer";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** 触发浏览器保存单个 Blob，并立即 revoke，避免 Object URL 泄漏。 */
export function triggerBlobDownload(blob: Blob, downloadName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = downloadName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 跨域下载：先走 nginx /storage（或 /media）同源反代，再 fetch 转 Blob 用 Object URL 下载。
 * 对象存储 CDN 直连会触发 CORS / mixed-content，转成同源代理路径后不再受限。
 */
export async function triggerCrossOriginDownload(fileUrl: string, downloadName?: string): Promise<boolean> {
  const original = String(fileUrl || "").trim();
  if (!original) return false;
  // 转同源代理路径：volces.com / myqcloud.com 等存储地址 → /storage/...（非缓存资源）
  const proxied = toProxiedDownloadUrl(original, false);
  const target = proxied || original;
  try {
    const res = await fetch(target);
    if (!res.ok) return false;
    const blob = await res.blob();
    const ext = (downloadName || original).split(".").pop()?.toLowerCase() || "";
    const safeName = downloadName || `download.${ext || "png"}`;
    triggerBlobDownload(blob, safeName);
    return true;
  } catch {
    return false;
  }
}

/** 智能下载：走同源代理下载，失败则退回直接 a 标签下载。 */
export async function downloadFileSmart(fileUrl: string, downloadName?: string): Promise<void> {
  const ok = await triggerCrossOriginDownload(fileUrl, downloadName);
  if (!ok) triggerUrlDownload(fileUrl, downloadName);
}

export function canUseDirectoryPicker(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as unknown as { showDirectoryPicker?: () => Promise<FileSystemDirectoryHandle> })
      .showDirectoryPicker === "function"
  );
}

/**
 * fetch 流式拉取后写入用户选定目录（避免 blob() 大文件长时间无网络活动像「卡死」）。
 * URL 经 processVideoUrl 转同源代理，并追加 cache bust 避免 304 空 body。
 */
/**
 * 获取不冲突的文件名：若同名文件已存在，追加 (1)、(2)… 后缀。
 */
async function getUniqueFileName(
  dirHandle: FileSystemDirectoryHandle,
  fileName: string
): Promise<string> {
  const extIndex = fileName.lastIndexOf(".");
  const base = extIndex > 0 ? fileName.slice(0, extIndex) : fileName;
  const ext = extIndex > 0 ? fileName.slice(extIndex) : "";

  let candidate = fileName;
  let counter = 1;
  while (true) {
    try {
      await dirHandle.getFileHandle(candidate);
      // 文件已存在，加序号
      candidate = `${base}(${counter})${ext}`;
      counter++;
    } catch {
      // 文件不存在，可以使用
      return candidate;
    }
  }
}

export async function writeUrlToDirectoryFile(
  rawUrl: string,
  fileName: string,
  directoryHandle: FileSystemDirectoryHandle,
  onProgress?: WriteUrlProgressCallback,
  externalSignal?: AbortSignal
): Promise<void> {
  const baseUrl = processVideoUrl(rawUrl);
  if (!baseUrl) {
    throw new Error("视频地址无效");
  }

  const url = appendCacheBust(baseUrl);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  // 合并外部 signal：外部 abort 时同步终止内部 controller
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  try {
    const response = await fetch(url, {
      cache: "reload",
      signal: controller.signal,
    });

    if (response.status === 304) {
      throw new Error("缓存响应无效，请重试");
    }
    if (!response.ok) {
      throw new Error(`下载失败: HTTP ${response.status}`);
    }

    const contentLength = response.headers.get("Content-Length");
    const totalBytes =
      contentLength && Number.isFinite(Number(contentLength))
        ? Number.parseInt(contentLength, 10)
        : null;

    const safeFileName = await getUniqueFileName(directoryHandle, fileName);
    const fileHandle = await directoryHandle.getFileHandle(safeFileName, { create: true });
    const writable = await (fileHandle as FileSystemFileHandleWithWritable).createWritable();

    try {
      const body = response.body;
      if (!body) {
        const blob = await response.blob();
        if (!blob.size) {
          throw new Error("文件为空");
        }
        onProgress?.(blob.size, blob.size);
        await writable.write(blob);
        await writable.close();
        return;
      }

      const reader = body.getReader();
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value?.byteLength) {
          await writable.write(value);
          loaded += value.byteLength;
          onProgress?.(loaded, totalBytes);
        }
      }

      await writable.close();

      if (loaded === 0) {
        throw new Error("文件为空");
      }
    } catch (writeErr: unknown) {
      // 下载中断或失败时，abort writable 截断文件并删除
      try {
        await writable.abort();
      } catch {
        /* ignore */
      }
      try {
        await directoryHandle.removeEntry(safeFileName);
      } catch {
        /* ignore */
      }
      throw writeErr;
    }
  } catch (e: unknown) {
    const err = e as { name?: string };
    if (err?.name === "AbortError") {
      throw new Error("下载超时，请检查网络后重试");
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

export type DownloadManyVideosOptions = {
  items: BatchVideoItem[];
  /**
   * @deprecated 不再打包 zip，保留参数避免大面积改调用签名
   */
  zipDownloadFileName?: string;
  /** 在用户选定目录下创建的子文件夹名（如「剧集名_英语」） */
  folderName?: string;
  onProgress?: (p: BatchDownloadProgress) => void;
};

/**
 * 批量下载：先让用户选择保存目录，再 fetch 逐集流式写入；进度按集数 + 当前集字节进度更新。
 */
export async function downloadManyVideosMemorySafe(
  options: DownloadManyVideosOptions
): Promise<BatchDownloadResult> {
  const { items, folderName, onProgress } = options;
  const total = items.length;
  let success = 0;
  let fail = 0;

  const report = (
    message: string,
    current: number,
    phase: BatchDownloadProgress["phase"] = "download",
    fileProgress?: number
  ) => {
    onProgress?.({ phase, current, total, message, fileProgress });
  };

  if (total === 0) {
    return { success: 0, fail: 0, usedZip: false, aborted: false };
  }

  if (!canUseDirectoryPicker()) {
    report("当前浏览器不支持选择文件夹保存", 0);
    return { success: 0, fail: total, usedZip: false, aborted: false, unsupportedDirectoryPicker: true };
  }

  const safeFolder = folderName ? sanitizeVideoFileBaseName(folderName) : "";

  try {
    report("请在下方的系统对话框中选择保存位置…", 0);
    const dirHandle = await (
      window as unknown as { showDirectoryPicker: () => Promise<FileSystemDirectoryHandle> }
    ).showDirectoryPicker();

    const writeRoot = safeFolder
      ? await dirHandle.getDirectoryHandle(safeFolder, { create: true })
      : dirHandle;

    report(`开始保存，共 ${total} 集`, 0);

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const fn = `${sanitizeVideoFileBaseName(it.baseName)}.mp4`;
      report(`正在连接第 ${i + 1}/${total} 集：${fn}`, success, "download", 0);

      try {
        await writeUrlToDirectoryFile(it.url, fn, writeRoot, (loaded, totalBytes) => {
          const fileProgress =
            totalBytes && totalBytes > 0 ? Math.min(loaded / totalBytes, 0.99) : undefined;
          const msg =
            totalBytes && totalBytes > 0
              ? `正在下载第 ${i + 1}/${total} 集：${fn}（${formatMb(loaded)}/${formatMb(totalBytes)} MB）`
              : `正在下载第 ${i + 1}/${total} 集：${fn}（已 ${formatMb(loaded)} MB）`;
          report(msg, success, "download", fileProgress);
        });
        success++;
        report(`已完成 ${success}/${total} 集`, success);
      } catch (e) {
        console.error(`保存失败 ${fn}:`, e);
        fail++;
        report(`第 ${i + 1} 集保存失败，继续下一集…`, success);
      }
    }

    report("全部处理完成", total, "done");
    return { success, fail, usedZip: false, aborted: false };
  } catch (e: unknown) {
    const err = e as { name?: string };
    if (err?.name === "AbortError") {
      return { success: 0, fail: 0, usedZip: false, aborted: true };
    }
    console.error("批量下载失败:", e);
    return { success, fail: fail + (total - success - fail), usedZip: false, aborted: false };
  }
}
